import { v2Request } from './client';
import { storageGet, storageSet } from '../platform/storage';
import { trackGa4AddToCart } from './firebaseAnalytics';

const CART_KEY = 'v2_cart';

/**
 * Fetch categories from v1-categories.
 * Returns [{ id, name, image, sortOrder, documentId }]
 */
export async function getCatalogCategories() {
  return v2Request('/api/categories');
}

/**
 * Fetch products, optionally filtered by category and branch stock.
 * @param {object} params
 * @param {string}  [params.category]  - category id e.g. "chicken"
 * @param {string}  [params.storeId]   - Firestore store doc id; adds isInStock per variant
 * @param {number}  [params.page=1]
 * @param {number}  [params.limit=20]
 */
export async function getProducts({ category, storeId, page = 1, limit = 20 } = {}) {
  const qs = new URLSearchParams({ status: 'active', page, limit });
  if (category) qs.set('category', category);
  if (storeId) qs.set('storeId', storeId);
  return v2Request(`/api/products?${qs.toString()}`);
}

/**
 * Search active products by name (debounced typeahead on home).
 * @param {object} params
 * @param {string} params.q
 * @param {string} [params.storeId]
 * @param {number} [params.limit=10]
 */
export async function searchProducts({ q, storeId, limit = 10 } = {}) {
  const term = String(q ?? '').trim();
  if (!term) return { data: [] };
  const qs = new URLSearchParams({ status: 'active', q: term, limit: String(limit) });
  if (storeId) qs.set('storeId', storeId);
  return v2Request(`/api/products?${qs.toString()}`);
}

export async function getProductById({ id, storeId } = {}) {
  const qs = new URLSearchParams();
  if (storeId) qs.set('storeId', storeId);
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  return v2Request(`/api/products/${id}${suffix}`);
}

// ---------------------------------------------------------------------------
// Cart — persisted shape:
// { products: [...], totalAmount, totalCount, coupon?: null | { couponCode, couponValue }, instructions?: string }
// Any line/qty/total mutation clears coupon; use applyCartCoupon to set it alone.
// ---------------------------------------------------------------------------

const CART_INSTRUCTIONS_MAX = 100;

function normalizeCoupon(raw) {
  if (!raw || typeof raw !== 'object') return null;
  const couponCode = String(raw.couponCode || '').trim().toUpperCase();
  const couponValue = Math.max(0, Number(raw.couponValue) || 0);
  if (!couponCode) return null;
  return { couponCode, couponValue };
}

function normalizeInstructions(raw) {
  const s = String(raw ?? '').trim();
  return s.slice(0, CART_INSTRUCTIONS_MAX);
}

function emptyCart() {
  return { products: [], totalAmount: 0, totalCount: 0, coupon: null, instructions: '' };
}

async function readCart() {
  const raw = await storageGet(CART_KEY);
  if (raw && Array.isArray(raw.products) && typeof raw.totalAmount === 'number' && typeof raw.totalCount === 'number') {
    return {
      products: raw.products,
      totalAmount: raw.totalAmount,
      totalCount: raw.totalCount,
      coupon: normalizeCoupon(raw.coupon),
      instructions: normalizeInstructions(raw.instructions),
    };
  }
  if (Array.isArray(raw)) {
    return emptyCart();
  }
  return emptyCart();
}

function notifyV2CartChanged() {
  try {
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('v2-cart-changed'));
    }
  } catch {
    // ignore
  }
}

async function writeCart(cart) {
  const payload = {
    products: Array.isArray(cart.products) ? cart.products : [],
    totalAmount: Number(cart.totalAmount) || 0,
    totalCount: Number(cart.totalCount) || 0,
    coupon: cart.coupon != null ? normalizeCoupon(cart.coupon) : null,
    instructions: normalizeInstructions(cart.instructions),
  };
  await storageSet(CART_KEY, payload);
  notifyV2CartChanged();
  return payload;
}

function toStoredOption(opt) {
  if (!opt) return null;
  return {
    id: opt.id,
    name: opt.name,
    sku: opt.sku,
    additionalPrice: String(opt.additionalPrice ?? '0'),
    sortOrder: opt.sortOrder ?? 0,
    isDefault: Boolean(opt.isDefault),
  };
}

/**
 * Map ProductDetail selection state → cart customization map (full option objects).
 * Checkbox groups: array of selections in state → stored as one object if single, else array of option objects.
 */
export function resolveCustomizationsForCart(productCustomizations, selected) {
  const out = {};
  const defs = productCustomizations || {};
  for (const [key, sel] of Object.entries(selected || {})) {
    const def = defs[key];
    if (!def?.options?.length) continue;

    if (Array.isArray(sel)) {
      const opts = sel
        .map((id) =>
          def.options.find(
            (o) =>
              String(o.id) === String(id) ||
              String(o.name) === String(id) ||
              String(o.sku).toUpperCase() === String(id).toUpperCase()
          )
        )
        .filter(Boolean)
        .map(toStoredOption);
      if (!opts.length) continue;
      out[key] = opts.length === 1 ? opts[0] : opts;
    } else if (sel != null && sel !== '') {
      const opt = def.options.find(
        (o) =>
          String(o.id) === String(sel) ||
          String(o.name) === String(sel) ||
          String(o.sku).toUpperCase() === String(sel).toUpperCase()
      );
      if (opt) out[key] = toStoredOption(opt);
    }
  }
  return out;
}

export function computeLineUnitPrice(variant, customizationsResolved) {
  let p = Number(variant?.price) || 0;
  for (const v of Object.values(customizationsResolved || {})) {
    const arr = Array.isArray(v) ? v : [v];
    for (const opt of arr) {
      p += Number(opt?.additionalPrice) || 0;
    }
  }
  return p;
}

/** One cart line per product (variant/customizations are not used to split lines). */
export function getCartLineKey({ productId }) {
  return String(productId ?? '');
}

function cartLinesForProduct(products, productId) {
  const pid = String(productId);
  return products
    .map((p, i) => ({ line: p, i }))
    .filter(({ line }) => String(line.productId) === pid);
}

function recomputeTotals(products) {
  let totalAmount = 0;
  let totalCount = 0;
  for (const line of products) {
    const unit = Number(line.price) || 0;
    const q = Number(line.quantity) || 0;
    totalAmount += unit * q;
    totalCount += q;
  }
  return { totalAmount, totalCount };
}

function stripVariantForCart(variant) {
  if (!variant || typeof variant !== 'object') return variant;
  const { isInStock, ...rest } = variant;
  return rest;
}

/** Join customization option names for compact display (e.g. cart row subtitle). */
export function formatCartCustomizationSummary(customizations) {
  if (!customizations || typeof customizations !== 'object') return '';
  const parts = [];
  for (const val of Object.values(customizations)) {
    const arr = Array.isArray(val) ? val : [val];
    for (const o of arr) {
      if (o?.name) parts.push(String(o.name));
    }
  }
  return parts.join(' · ');
}

/**
 * Flat list of chosen add-ons with optional extra price (for order summaries).
 * @returns {{ name: string, additionalPrice: number }[]}
 */
/** Sum of customization add-on prices across all cart lines (× qty). */
export function computeCartCustomizationTotal(products) {
  if (!Array.isArray(products)) return 0;
  let total = 0;
  for (const line of products) {
    const qty = Number(line.quantity) || 0;
    const addOns = listCartCustomizationAddOns(line.customizations);
    const perUnit = addOns.reduce((s, a) => s + (Number(a.additionalPrice) || 0), 0);
    total += perUnit * qty;
  }
  return total;
}

export function listCartCustomizationAddOns(customizations) {
  const out = [];
  if (!customizations || typeof customizations !== 'object') return out;
  for (const val of Object.values(customizations)) {
    const arr = Array.isArray(val) ? val : [val];
    for (const o of arr) {
      if (!o?.name) continue;
      out.push({
        name: String(o.name),
        additionalPrice: Number(o.additionalPrice) || 0,
      });
    }
  }
  return out;
}

export function getAppliedCouponDiscountRupee(cart) {
  const c = cart?.coupon;
  if (!c || !c.couponCode) return 0;
  const v = Math.max(0, Number(c.couponValue) || 0);
  const sub = Math.max(0, Number(cart.totalAmount) || 0);
  return Math.min(sub, v);
}

/** SKUs / ids to send to Pilot coupon validation (variant SKU + product id). */
export function cartSkusForCouponValidation(products) {
  const out = [];
  for (const line of products || []) {
    const sku = line?.variant?.sku != null ? String(line.variant.sku).trim() : '';
    if (sku) out.push(sku);
    const pid = line?.productId != null ? String(line.productId).trim() : '';
    if (pid) out.push(pid);
  }
  return [...new Set(out.map((s) => s.toUpperCase()))];
}

/** Full cart document (for Cart page / checkout). */
export async function getCart() {
  return readCart();
}

/**
 * Persist delivery instructions on the v2_cart document (does not clear coupon or lines).
 * @param {string} text
 */
export async function setCartInstructions(text) {
  const cart = await readCart();
  cart.instructions = normalizeInstructions(text);
  return writeCart(cart);
}

/**
 * Persist a validated coupon on the current cart (does not clear lines).
 * @param {{ couponCode: string, couponValue: number }} coupon
 */
export async function applyCartCoupon(coupon) {
  const cart = await readCart();
  const norm = normalizeCoupon(coupon);
  if (!norm) {
    cart.coupon = null;
    return writeCart(cart);
  }
  cart.coupon = norm;
  return writeCart(cart);
}

/** Remove coupon only (cart lines unchanged). */
export async function clearCartCoupon() {
  const cart = await readCart();
  if (!cart.coupon) return cart;
  cart.coupon = null;
  return writeCart(cart);
}

/** Clear persisted V2 cart and notify listeners. */
export async function clearCart() {
  return writeCart(emptyCart());
}

/** @deprecated use getCart */
export async function getCartItems() {
  const cart = await readCart();
  return cart.products;
}

export async function getCartLineQuantity({ productId }) {
  const cart = await readCart();
  const matches = cartLinesForProduct(cart.products, productId);
  return matches.reduce((sum, { line }) => sum + (Number(line.quantity) || 0), 0);
}

/** First cart line for this product (one line per product). */
export async function getCartLineForProduct(productId) {
  const cart = await readCart();
  const matches = cartLinesForProduct(cart.products, productId);
  return matches[0]?.line ?? null;
}

export async function setCartLineQuantity({ productId }, qty) {
  const cart = await readCart();
  const matches = cartLinesForProduct(cart.products, productId);
  if (!matches.length) return cart;

  const q = Number(qty) || 0;
  const pid = String(productId);
  const template = matches[0].line;
  cart.products = cart.products.filter((p) => String(p.productId) !== pid);
  if (q > 0) {
    cart.products.push({ ...template, quantity: q });
  }

  const { totalAmount, totalCount } = recomputeTotals(cart.products);
  cart.totalAmount = totalAmount;
  cart.totalCount = totalCount;
  cart.coupon = null;
  return writeCart(cart);
}

/**
 * Add or merge a line. Pass resolved customizations (full option objects per key).
 * @param {object} payload
 * @param {string} payload.productId
 * @param {string} payload.name
 * @param {string} payload.imageUrl
 * @param {object} payload.variant
 * @param {Record<string, object|object[]>} payload.customizations
 * @param {number} [payload.qty=1]
 */
export async function addToCartItem(payload) {
  const {
    productId,
    name,
    imageUrl,
    variant,
    customizations,
    qty = 1,
  } = payload;

  const cart = await readCart();
  const customizationsResolved = customizations ?? {};
  const linePrice = computeLineUnitPrice(variant, customizationsResolved);
  const storedVariant = stripVariantForCart(variant);

  const addQty = Number(qty) || 1;
  const pid = String(productId);

  let existingQty = 0;
  cart.products = cart.products.filter((p) => {
    if (String(p.productId) === pid) {
      existingQty += Number(p.quantity) || 0;
      return false;
    }
    return true;
  });

  const isNewLine = existingQty === 0;

  cart.products.push({
    productId,
    name,
    imageUrl,
    quantity: existingQty + addQty,
    price: linePrice,
    variant: storedVariant,
    customizations: customizationsResolved,
  });

  const { totalAmount, totalCount } = recomputeTotals(cart.products);
  cart.totalAmount = totalAmount;
  cart.totalCount = totalCount;
  cart.coupon = null;

  if (isNewLine) {
    trackGa4AddToCart({
      productId,
      name,
      price: linePrice,
      quantity: addQty,
    });
  }

  return writeCart(cart);
}
